import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthBackendService } from '../../app/services/auth-backend.service';
import {
	ClearErrorMessage,
	GoToLoginWithRedirect,
	Login,
	Logout,
	SetErrorMessage,
} from './auth.action';
import {
	AuthStateModel,
	ROLE_BASIC_RECORDS,
	ROLE_CRISIS_TEAM,
	ROLE_DB_ADMIN,
	ROLE_DEAN,
	ROLE_FINANCE,
	ROLE_HOUSING,
	ROLE_HR,
	ROLE_MANAGER,
	ROLE_PHONE,
	ROLE_REPORTS,
	ROLE_TRAVEL_ADMIN,
	ROLE_USER,
} from './auth.model';
import { StartLoading, StopLoading } from './main.action';

export const DEFAULT_STATE: AuthStateModel = {
	netId: null,
	userId: null,
	loggedIn: false,
	errorMessage: null,
	roles: [],
	redirectUrl: null,
};

@State<AuthStateModel>({
	name: 'auth',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class AuthState {
	@Selector()
	static authState(state: AuthStateModel): AuthStateModel {
		return state;
	}

	@Selector()
	static userId(state: AuthStateModel): number {
		return state.userId;
	}

	@Selector()
	static errorMessage(state: AuthStateModel): string {
		return state.errorMessage;
	}

	@Selector()
	static loggedIn(state: AuthStateModel): boolean {
		return state.loggedIn;
	}

	@Selector()
	static isTravelAdmin(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_TRAVEL_ADMIN);
	}

	@Selector()
	static isManager(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_MANAGER);
	}

	@Selector()
	static isDean(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_DEAN);
	}

	@Selector()
	static isFinance(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_FINANCE);
	}

	@Selector()
	static isAdministrative(state: AuthStateModel): boolean {
		return AuthState.isDean(state) || AuthState.isFinance(state);
	}

	@Selector()
	static hasTravelQueue(state: AuthStateModel): boolean {
		return this.isTravelAdmin(state) || this.isAdministrative(state);
	}

	@Selector()
	static isBasicRecords(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_BASIC_RECORDS);
	}

	@Selector()
	static isHR(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_HR);
	}

	@Selector()
	static canViewFullUser(state: AuthStateModel): boolean {
		return (
			AuthState.hasRole(state, ROLE_HR) ||
			AuthState.hasRole(state, ROLE_CRISIS_TEAM) ||
			AuthState.hasRole(state, ROLE_HOUSING)
		);
	}

	@Selector()
	static hasUserResource(state: AuthStateModel): boolean {
		return (
			AuthState.hasRole(state, ROLE_HR) ||
			AuthState.hasRole(state, ROLE_BASIC_RECORDS) ||
			AuthState.hasRole(state, ROLE_CRISIS_TEAM) ||
			AuthState.hasRole(state, ROLE_HOUSING)
		);
	}

	@Selector()
	static canEditEmergencyInfo(state: AuthStateModel): boolean {
		return (
			AuthState.hasRole(state, ROLE_HR) ||
			AuthState.hasRole(state, ROLE_CRISIS_TEAM)
		);
	}

	@Selector()
	static canEditQatarInfo(state: AuthStateModel): boolean {
		return (
			AuthState.hasRole(state, ROLE_HR) ||
			AuthState.hasRole(state, ROLE_HOUSING)
		);
	}

	@Selector()
	static isAdmin(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_DB_ADMIN);
	}

	@Selector()
	static isCrisisTeam(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_CRISIS_TEAM);
	}

	@Selector()
	static isCallingTree(state: AuthStateModel): boolean {
		return (
			AuthState.hasRole(state, ROLE_CRISIS_TEAM) ||
			AuthState.hasRole(state, ROLE_HR)
		);
	}

	@Selector()
	static isPhone(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_PHONE);
	}

	@Selector()
	static isHousing(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_HOUSING);
	}

	@Selector()
	static isReports(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_REPORTS);
	}

	@Selector()
	static isUser(state: AuthStateModel): boolean {
		return AuthState.hasRole(state, ROLE_USER);
	}

	@Selector()
	static isApprover(state: AuthStateModel): boolean {
		return (
			AuthState.isManager(state) ||
			AuthState.isDean(state) ||
			AuthState.isFinance(state) ||
			AuthState.isHR(state)
		);
	}

	@Selector()
	static isTravelQueueViewer(state: AuthStateModel): boolean {
		return (
			AuthState.isManager(state) ||
			AuthState.isTravelAdmin(state) ||
			AuthState.isDean(state) ||
			AuthState.isFinance(state)
		);
	}

	@Selector()
	static advancedSearch(state: AuthStateModel): boolean {
		return (
			AuthState.isManager(state) ||
			AuthState.isTravelAdmin(state) ||
			AuthState.isDean(state) ||
			AuthState.isFinance(state) ||
			AuthState.isHR(state) ||
			AuthState.isCrisisTeam(state)
		);
	}

	@Selector()
	static showUserHeaderMenu(state: AuthStateModel): boolean {
		return (
			AuthState.isHR(state) ||
			AuthState.isBasicRecords(state) ||
			AuthState.isCrisisTeam(state) ||
			AuthState.isAdmin(state)
		);
	}

	@Selector()
	static canArchiveOrActivateUser(state: AuthStateModel): boolean {
		return AuthState.isHR(state) || AuthState.isBasicRecords(state);
	}

	@Selector()
	static redirectUrl(state: AuthStateModel): string {
		return state.redirectUrl;
	}

	private static hasRole(state: AuthStateModel, role: string): boolean {
		return state.roles.indexOf(role) !== -1;
	}

	constructor(
		private authBackend: AuthBackendService,
		private router: Router,
		private ngZone: NgZone
	) {}

	/**
	 * Login
	 * ======
	 * Attempts to authenticate against the backend, updates the state,
	 * and then routes to the dashboard if valid.
	 */
	@Action(Login)
	login(ctx: StateContext<AuthStateModel>, action: Login) {
		ctx.dispatch([new StartLoading(), new ClearErrorMessage()]);
		return this.authBackend
			.login({ netId: action.netId, password: action.password })
			.pipe(
				map((response) => {
					ctx.patchState({
						netId: action.netId,
						userId: response.userId,
						loggedIn: true,
						roles: response.roles,
					});
					ctx.dispatch([new StopLoading()]);
					const redirectUrl = ctx.getState().redirectUrl;
					if (redirectUrl) {
						this.ngZone.run(() =>
							this.router.navigateByUrl(redirectUrl)
						);
						ctx.patchState({ redirectUrl: null });
					} else {
						this.ngZone.run(() =>
							this.router.navigate(['tacit', 'dashboard'])
						);
					}
				}),
				catchError((error) => {
					ctx.dispatch([
						new SetErrorMessage(error),
						new StopLoading(),
					]);
					return of({});
				})
			);
	}

	/**
	 * Logout
	 * =======
	 * Attempts to logout on backend, updates the state,
	 * and then routes to the login page.
	 */
	@Action(Logout)
	logout(ctx: StateContext<AuthStateModel>, _action: Logout) {
		this.ngZone.run(() => this.router.navigate(['login']));
		ctx.patchState(DEFAULT_STATE);
		return this.authBackend.logout();
	}

	/**
	 * SetErrorMessage: Updates `errorMessage` with a provided message.
	 */
	@Action(SetErrorMessage)
	setErrorMessage(
		ctx: StateContext<AuthStateModel>,
		action: SetErrorMessage
	) {
		ctx.patchState({
			errorMessage: action.errorMessage,
		});
	}

	/**
	 * ClearErrorMessage: Sets `errorMessage` to null
	 */
	@Action(ClearErrorMessage)
	clearErrorMessage(
		ctx: StateContext<AuthStateModel>,
		_action: ClearErrorMessage
	) {
		ctx.patchState({
			errorMessage: null,
		});
	}

	@Action(GoToLoginWithRedirect)
	goToLoginWithRedirect(
		ctx: StateContext<AuthStateModel>,
		action: GoToLoginWithRedirect
	) {
		ctx.patchState({ redirectUrl: action.redirectUrl });
		this.ngZone.run(() => this.router.navigate(['login']));
	}
}
